import React from 'react';
import PropTypes from 'prop-types';
import Component from '@ux/component';
import addhoc from 'addhoc';

// Default Namespace functionality
const defaultNamespace = (...c) => c.filter(Boolean).join(' ');

/**
 * React context to store namespace functionality
 *
 * @const {React.Context} NamespaceContext
 * @public
 */
export const NamespaceContext = React.createContext({
  namespace: defaultNamespace
});

const withNamespaceContext = addhoc(getWrappedComponent =>
  <NamespaceContext.Consumer>
    { namespaceContext => getWrappedComponent({ namespace: namespaceContext.namespace }) }
  </NamespaceContext.Consumer>, 'WithNamespace');

/**
 * Higher-Order Component function to provide namespace functionality.
 * This replaces the prior NamespaceConsumer abstract class.
 *
 * @param {React.Component} WrappedComponent The component to be augmented with namespace functionality
 * @param {string} [includeOriginal='OriginalComponent'] The optional property name
 * that the original component can be included on.
 * @returns {React.Component} A wrapped component class that provides a `namespace` function as a prop
 * @public
 * @example
 * import { withNamespace, NamespaceShape, NamespaceConsumer } from '@ux/namespace-component';
 * class MyComponent extends NamespaceConsumer {
 *   render() {
 *     return (
 *       <span className={ this.namespace('some-class-name') }>foo</span>
 *     );
 *   }
 * }
 * MyComponent.propTypes = {
 *   ...NamespaceShape
 * };
 *
 * export default withNamespace(MyComponent);
 */
export const withNamespace = function withNamespace(WrappedComponent, includeOriginal = 'OriginalComponent') {
  const hocComponent = withNamespaceContext(WrappedComponent);

  // Optionally attach the original component to the hoc
  if (includeOriginal) {
    hocComponent[includeOriginal] = WrappedComponent;
  }

  return hocComponent;
};

/**
 * Shape of props added to components wrapped with {@link withNamespace}.
 *
 * @const {Object} NamespaceShape
 * @public
 */
export const NamespaceShape = {
  namespace: PropTypes.func.isRequired
};

/**
 * Simple class to provide inheriting component with a utility `this.namespace` method that simply calls through to
 * this.props.namespace
 *
 * @public
 */
export class NamespaceConsumer extends Component {
  constructor() {
    super(...arguments);

    this.namespace = this.namespace.bind(this);
  }

  namespace(/* args */) {
    if (!this.props.namespace) {
      this.debug('`namespace` prop is undefined. Did you wrap this component using withNamespace()?');
      return defaultNamespace(...arguments);
    }

    return this.props.namespace(...arguments);
  }
}

NamespaceConsumer.propTypes = {
  ...NamespaceShape
};

/**
 * Provide additional namespacing logic on top of @ux/component. Useful for
 * custom styling. Used by headers for example.
 *
 * @public
 */
export class NamespaceProvider extends Component {
  constructor(props = {}) {
    super(...arguments);

    if (props.namespace) {
      this._namespace = props.namespace;
    }

    this.state = {
      contextValue: {
        namespace: this.namespace
      }
    };
  }

  /**
   * Return namespaced value. Prefer context over the regular function,
   * context will be properly scoped and has access to `_namespace`.
   *
   * @returns {String} namespace.
   * @public
   */
  get namespace() {
    return (...str) => {
      str = str.filter(Boolean);

      if (!this._namespace) {
        return str.join(' ');
      }

      if (!str.length) {
        return;
      }

      //
      // Handle the case where a single string is provided with multiple entries.
      //
      if (str.length === 1 && ~str[0].indexOf(' ')) {
        str = str[0].split(' ');
      }

      return str
        .map(s => `${this._namespace}-${s}`)
        .join(' ');
    };
  }

  /**
   * Set namespace for local.
   *
   * @param {String} name Namespace.
   * @public
   */
  set namespace(name) {
    this.debug('Setting namespace to %s', name);

    this._namespace = name;
  }

  /**
   * Wrap a single component to provide the namespace through context.
   *
   * @returns {ReactElement} Single React element.
   * @private
   */
  render() {
    return (
      <NamespaceContext.Provider value={ this.state.contextValue }>
        { this.props.children }
      </NamespaceContext.Provider>
    );
  }
}

/**
 * Define properties.
 *
 * @type {Object}
 * @public
 */
NamespaceProvider.propTypes = {
  /** The namespace to use */
  namespace: PropTypes.string,
  /** Child components */
  children: PropTypes.node
};



// WEBPACK FOOTER //
// ./node_modules/@ux/namespace-component/index.js